import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5070').replace(/\/api\/?$/, '');


export const PodiumF1 = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPodium = async () => {
    try {
      const res = await fetch(`${API_URL}/api/points/podium`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data);
      }
    } catch (err) {
      console.error('Error al cargar el podio:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPodium();
    const socket = io(API_URL);
    socket.on('podium_update', () => {
      fetchPodium();
    });
    return () => socket.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500 font-medium">
        Cargando la información del podio en tiempo real...
      </div>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <div className="p-12 text-center bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
        <div className="text-4xl mb-3">🏁</div>
        <h4 className="text-lg font-semibold text-gray-700">No hay usuarios con puntos registrados</h4>
        <p className="text-xs text-gray-400 mt-1">
          A medida que los usuarios escaneen los códigos QR de las estaciones, aparecerán automáticamente reflejados en este podio.
        </p>
      </div>
    );
  }

  const first = leaderboard[0];
  const second = leaderboard[1];
  const third = leaderboard[2];
  const rest = leaderboard.slice(3);

  return (
    <div className="space-y-8">
      {/* Formula 1 Podium (Top 3) */}
      <div className="flex justify-center items-end gap-3 sm:gap-6 pt-8 pb-4">
        {/* 2nd Place */}
        {second ? (
          <div className="flex flex-col items-center w-28 sm:w-36">
            <div className="text-center mb-2">
              <span className="inline-block bg-slate-200 text-slate-700 text-xs font-bold px-2 py-0.5 rounded-full mb-1">
                2° Lugar
              </span>
              <p className="font-bold text-gray-800 text-sm truncate max-w-[120px]" title={second.full_name}>
                {second.full_name}
              </p>
              <p className="text-xs font-extrabold text-purple-600">{second.total_points} pts</p>
            </div>
            <div className="w-full bg-gradient-to-t from-slate-400 to-slate-300 h-32 rounded-t-2xl shadow-md border-t-4 border-slate-200 flex items-center justify-center text-white text-3xl font-black">
              2
            </div>
          </div>
        ) : (
          <div className="w-28 sm:w-36 h-32 bg-gray-100/50 rounded-t-2xl border-t-4 border-gray-200 flex items-center justify-center text-gray-300 font-bold text-2xl">
            -
          </div>
        )}

        {/* 1st Place (F1 Highest Podium) */}
        {first && (
          <div className="flex flex-col items-center w-32 sm:w-44 -mt-6">
            <div className="text-center mb-2">
              <div className="text-3xl mb-1 animate-bounce">👑</div>
              <span className="inline-block bg-amber-400 text-amber-950 text-xs font-bold px-2.5 py-0.5 rounded-full shadow-sm mb-1">
                1° Lugar
              </span>
              <p className="font-extrabold text-gray-900 text-base truncate max-w-[150px]" title={first.full_name}>
                {first.full_name}
              </p>
              <p className="text-sm font-black text-purple-700">{first.total_points} pts</p>
            </div>
            <div className="w-full bg-gradient-to-t from-amber-500 to-yellow-400 h-44 rounded-t-2xl shadow-xl border-t-4 border-yellow-200 flex items-center justify-center text-white text-4xl font-black">
              1
            </div>
          </div>
        )}

        {/* 3rd Place */}
        {third ? (
          <div className="flex flex-col items-center w-28 sm:w-36">
            <div className="text-center mb-2">
              <span className="inline-block bg-amber-700/20 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full mb-1">
                3° Lugar
              </span>
              <p className="font-bold text-gray-800 text-sm truncate max-w-[120px]" title={third.full_name}>
                {third.full_name}
              </p>
              <p className="text-xs font-extrabold text-purple-600">{third.total_points} pts</p>
            </div>
            <div className="w-full bg-gradient-to-t from-amber-700 to-amber-600 h-24 rounded-t-2xl shadow-md border-t-4 border-amber-500 flex items-center justify-center text-white text-2xl font-black">
              3
            </div>
          </div>
        ) : (
          <div className="w-28 sm:w-36 h-24 bg-gray-100/50 rounded-t-2xl border-t-4 border-gray-200 flex items-center justify-center text-gray-300 font-bold text-xl">
            -
          </div>
        )}
      </div>

      {/* Leaderboard Table (Positions 4 to 10) */}
      {rest.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 font-semibold text-xs text-gray-500 uppercase tracking-wider">
            Posiciones Restantes (4° al 10°)
          </div>
          <div className="divide-y divide-gray-100">
            {rest.map((user, idx) => (
              <div key={user.user_id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center">
                    {idx + 4}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{user.full_name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm font-black text-purple-600">{user.total_points} pts</span>
                  <p className="text-[10px] text-gray-400">
                    {user.participation_points}p / {user.skill_points}d ({user.stations_visited} registros)
                  </p>
                </div>

              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
